import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
	Alert,
	Box,
	Button,
	Chip,
	FormControl,
	IconButton,
	InputLabel,
	Link,
	MenuItem,
	Paper,
	Select,
	Skeleton,
	Stack,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tabs,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { SectionInfoTooltip } from 'features/salesDashboard/components/SectionInfoTooltip';
import { useCreditAssignments, useCreditTerms } from '../../hooks/useCreditRules';
import { formatFecha } from '../../helpers/describe';
import { catalogoTooltip, condicionCreditoTooltip, dosEjesTooltip } from '../../constants';
import { ICreditTerm } from '../../types';
import { CreditTermSummary } from '../CreditTermSummary';
import { CreditTermFormDialog } from '../CreditTermFormDialog';

/** Nombre de la agencia por id, para no mostrar números sueltos. */
const AGENCY_LABEL: Record<number, string> = {
	1: 'Uruguay',
	2: 'Colombia',
	3: 'Paraguay',
	4: 'Bolivia',
	5: 'TN Paraguay',
};

// ─── Pestaña: clientes ───────────────────────────────────────────────────────

function ClientesTab(): JSX.Element {
	const [search, setSearch] = useState('');
	const [debounced, setDebounced] = useState('');
	const [agencyId, setAgencyId] = useState<number | ''>('');
	const [termId, setTermId] = useState<number | ''>('');

	// Debounce del buscador: no disparamos una request por tecla.
	React.useEffect(() => {
		const t = setTimeout(() => setDebounced(search.trim()), 350);
		return () => clearTimeout(t);
	}, [search]);

	const { data: terms } = useCreditTerms();
	const { data, loading, error } = useCreditAssignments({
		agencyId: agencyId === '' ? undefined : agencyId,
		termId: termId === '' ? undefined : termId,
		search: debounced || undefined,
		limit: 100,
	});

	return (
		<>
			<Box display="flex" gap={1.5} flexWrap="wrap" mb={2}>
				<TextField
					size="small"
					placeholder="Buscar por nombre o documento…"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 18, mr: 0.5, color: 'text.disabled' }} /> }}
					sx={{ minWidth: 280 }}
				/>
				<FormControl size="small" sx={{ minWidth: 150 }}>
					<InputLabel>Agencia</InputLabel>
					<Select
						value={agencyId}
						label="Agencia"
						onChange={(e) => setAgencyId(e.target.value === '' ? '' : Number(e.target.value))}
					>
						<MenuItem value="">Todas</MenuItem>
						{Object.entries(AGENCY_LABEL).map(([id, label]) => (
							<MenuItem key={id} value={Number(id)}>
								{label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<FormControl size="small" sx={{ minWidth: 190 }}>
					<InputLabel>Condición</InputLabel>
					<Select
						value={termId}
						label="Condición"
						onChange={(e) => setTermId(e.target.value === '' ? '' : Number(e.target.value))}
					>
						<MenuItem value="">Todas</MenuItem>
						{(terms ?? []).map((t) => (
							<MenuItem key={t.id} value={t.id}>
								{t.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Box>

			{loading ? (
				<Skeleton variant="rectangular" height={280} />
			) : error ? (
				<Alert severity="error">{error}</Alert>
			) : !data?.assignments.length ? (
				<Typography variant="body2" color="text.secondary">
					No hay clientes con condición asignada para estos filtros.
				</Typography>
			) : (
				<>
					<Typography variant="caption" color="text.secondary" display="block" mb={1}>
						{data.pagination.total} cliente{data.pagination.total !== 1 ? 's' : ''} con condición asignada
					</Typography>
					<TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 560, overflow: 'auto' }}>
						<Table size="small" stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell>Cliente</TableCell>
									<TableCell>Documento</TableCell>
									<TableCell>Agencia</TableCell>
									<TableCell>Condición</TableCell>
									<TableCell>Vigente desde</TableCell>
									<TableCell />
								</TableRow>
							</TableHead>
							<TableBody>
								{data.assignments.map((a) => (
									<TableRow key={a.id} hover>
										<TableCell sx={{ maxWidth: 260 }}>
											<Typography variant="body2" fontWeight={500} noWrap title={a.passengerName ?? ''}>
												{a.passengerName ?? a.passengerId}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body2" color="text.secondary">
												{a.passengerDocument ?? '—'}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body2" color="text.secondary">
												{AGENCY_LABEL[a.agencyId] ?? a.agencyId}
											</Typography>
										</TableCell>
										<TableCell>
											<Chip label={a.termName ?? `#${a.termId}`} size="small" variant="outlined" />
										</TableCell>
										<TableCell sx={{ whiteSpace: 'nowrap' }}>
											<Typography variant="body2" color="text.secondary">
												{formatFecha(a.vigenteDesde)}
											</Typography>
										</TableCell>
										<TableCell>
											<Link
												component={RouterLink}
												to={`/passengers/${a.passengerId}?from=customers`}
												target="_blank"
												rel="noopener"
												underline="hover"
												variant="caption"
												sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, whiteSpace: 'nowrap' }}
											>
												Ver cliente
												<OpenInNewIcon sx={{ fontSize: 13 }} />
											</Link>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
					{data.pagination.total > data.assignments.length && (
						<Typography variant="caption" color="text.disabled" display="block" mt={1}>
							Mostrando {data.assignments.length} de {data.pagination.total}. Afiná la búsqueda para ver el resto.
						</Typography>
					)}
				</>
			)}
		</>
	);
}

// ─── Pestaña: catálogo de condiciones ────────────────────────────────────────

function CondicionesTab(): JSX.Element {
	const { data: terms, loading, error, refetch } = useCreditTerms(true);
	const [formOpen, setFormOpen] = useState(false);
	const [editing, setEditing] = useState<ICreditTerm | null>(null);

	const abrir = (t: ICreditTerm | null) => {
		setEditing(t);
		setFormOpen(true);
	};

	return (
		<>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
				<Typography variant="caption" color="text.secondary">
					{terms?.length ?? 0} condici{terms?.length === 1 ? 'ón' : 'ones'} en el catálogo
				</Typography>
				<Button
					size="small"
					variant="contained"
					startIcon={<AddIcon sx={{ fontSize: 16 }} />}
					onClick={() => abrir(null)}
					sx={{ textTransform: 'none' }}
				>
					Nueva condición
				</Button>
			</Box>

			{loading ? (
				<Skeleton variant="rectangular" height={280} />
			) : error ? (
				<Alert severity="error">{error}</Alert>
			) : !terms?.length ? (
				<Alert severity="info">
					Todavía no hay condiciones cargadas. Creá la primera con el botón de arriba: se definen una vez y después se
					asignan a los clientes desde su ficha.
				</Alert>
			) : (
				<Stack spacing={1.5}>
					{terms.map((t) => (
						<Paper key={t.id} variant="outlined" sx={{ p: 2, opacity: t.enabled ? 1 : 0.6 }}>
							<Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
								<Box flex={1} minWidth={260}>
									<Box display="flex" alignItems="center" gap={0.75} mb={1}>
										<Typography variant="subtitle2" fontWeight={700}>
											{t.name}
										</Typography>
										{!t.enabled && <Chip label="Deshabilitada" size="small" sx={{ fontSize: 10 }} />}
									</Box>
									{t.description && (
										<Typography variant="caption" color="text.secondary" display="block" mb={1}>
											{t.description}
										</Typography>
									)}
									<CreditTermSummary term={t} />
								</Box>
								<Box display="flex" alignItems="center" gap={0.5}>
									<Chip
										label={`${t.clientesAsignados ?? 0} cliente${t.clientesAsignados === 1 ? '' : 's'}`}
										size="small"
										color={t.clientesAsignados ? 'primary' : 'default'}
										variant="outlined"
									/>
									<Tooltip
										title={
											t.clientesAsignados
												? `Editarla cambia cómo se factura a ${t.clientesAsignados} cliente(s)`
												: 'Editar condición'
										}
									>
										<IconButton size="small" onClick={() => abrir(t)}>
											<EditIcon sx={{ fontSize: 17 }} />
										</IconButton>
									</Tooltip>
								</Box>
							</Box>
						</Paper>
					))}
				</Stack>
			)}

			<CreditTermFormDialog open={formOpen} onClose={() => setFormOpen(false)} term={editing} onSaved={refetch} />
		</>
	);
}

// ─── Panel principal ─────────────────────────────────────────────────────────

/**
 * Pantalla de gestión de condiciones de crédito. Reemplaza el excel: qué
 * condiciones existen y qué cliente tiene cada una. La edición por cliente vive
 * en la ficha del cliente (que es donde uno la busca), acá está la vista global.
 */
export function CreditRulesAdmin(): JSX.Element {
	const [tab, setTab] = useState(0);

	return (
		<>
			<Alert severity="info" sx={{ mb: 2 }}>
				La condición de un cliente se edita desde <strong>su ficha</strong> (Clientes → ver cliente → Condición de
				crédito). Esta pantalla es la vista global para revisar y auditar.
			</Alert>

			<Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
				<Tab
					label={
						<Box display="inline-flex" alignItems="center" gap={0.5}>
							Clientes
							<SectionInfoTooltip content={condicionCreditoTooltip} />
						</Box>
					}
					sx={{ textTransform: 'none' }}
				/>
				<Tab
					label={
						<Box display="inline-flex" alignItems="center" gap={0.5}>
							Condiciones
							<SectionInfoTooltip content={catalogoTooltip} />
						</Box>
					}
					sx={{ textTransform: 'none' }}
				/>
			</Tabs>

			{tab === 0 ? <ClientesTab /> : <CondicionesTab />}

			{tab === 1 && (
				<Box mt={2} display="inline-flex" alignItems="center" gap={0.5}>
					<Typography variant="caption" color="text.secondary">
						¿Cómo se compone una condición?
					</Typography>
					<SectionInfoTooltip content={dosEjesTooltip} />
				</Box>
			)}
		</>
	);
}
